import React, { useState, useEffect } from "react";
import CardBase from "@/components/cards/CardBase";
import { Input } from "@/components/ui/InputDefault";
import { Label } from "@/components/ui/label";

interface FormField {
  key: string;
  label: string;
  type: 'text' | 'number' | 'textarea';
  value: string | number;
  placeholder?: string;
}

interface FormSection {
  title: string;
  fields: FormField[];
}

interface FormWidgetProps {
  title: string;
  sections: FormSection[];
  onDataChange?: (data: Record<string, string | number>) => void;
  showTitle?: boolean;
  backgroundColor?: string;
}

const FormWidget: React.FC<FormWidgetProps> = ({
  title,
  sections,
  onDataChange,
  showTitle = true,
  backgroundColor
}) => {
  const [formData, setFormData] = useState<Record<string, string | number>>({});

  // Initialize form data from sections
  useEffect(() => {
    const initialData: Record<string, string | number> = {};
    sections.forEach(section => {
      section.fields.forEach(field => {
        initialData[field.key] = field.value;
      });
    });
    setFormData(initialData);
  }, [sections]);

  const handleFieldChange = (key: string, value: string | number) => {
    const newData = { ...formData, [key]: value };
    setFormData(newData);
    // Note: onDataChange is no longer called here - it will be called on blur
  };

  const handleFieldBlur = (key: string) => {
    // Save changes when focus is lost
    if (onDataChange) {
      onDataChange(formData);
    }
  };

  const renderField = (field: FormField) => {
    const commonProps = {
      id: field.key,
      value: formData[field.key] || field.value,
      onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const value = field.type === 'number' ? Number(e.target.value) : e.target.value;
        handleFieldChange(field.key, value);
      },
      onBlur: () => handleFieldBlur(field.key),
      placeholder: field.placeholder,
      className: "w-full"
    };

    switch (field.type) {
      case 'textarea':
        return (
          <textarea
            {...commonProps}
            rows={3}
            className="w-full resize-none border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        );
      case 'number':
        return (
          <Input
            {...commonProps}
            type="number"
            min={0}
          />
        );
      default:
        return <Input {...commonProps} type="text" />;
    }
  };

  return (
    <CardBase className="flex flex-col h-full w-full flex-1">
      {showTitle && (
        <div className="p-4 pb-2 border-b border-gray-300">
          <span className="text-lg font-semibold text-gray-900">{title}</span>
        </div>
      )}
      
      <div className={`${showTitle ? 'p-4 pt-2' : 'p-4'} flex flex-col gap-4 flex-1 w-full overflow-y-auto`}>
        {sections.map((section, sectionIndex) => (
          <div key={sectionIndex} className="space-y-3">
            <h3 className="text-sm font-medium text-gray-700 border-b border-gray-200 pb-1">
              {section.title}
            </h3>
            <div className="space-y-3">
              {section.fields.map((field) => (
                <div key={field.key} className="space-y-1">
                  <Label htmlFor={field.key} className="text-sm font-medium text-gray-600">
                    {field.label}
                  </Label>
                  {renderField(field)}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </CardBase>
  );
};

export default FormWidget; 